import { useCallback, useEffect, useState } from 'react'
import { guaranies } from '../datos/formato.js'
import { TituloPagina } from '../comunes/AppShell.js'
import { Boton } from '../comunes/Boton.js'
import { DuracionesProfesional } from './DuracionesProfesional.js'
import {
  CatalogoEspecialidades,
  EspecialidadesProfesional,
  SelectorEspecialidadRequerida,
} from './Especialidades.js'

/**
 * Configuración de la clínica (rol admin).
 *
 * **Excepción declarada a "la UI lee sólo de Dexie" (§5.5).** Y es a propósito:
 * la configuración no es trabajo de agenda. Se toca una vez al dar de alta la
 * clínica y después cada varios meses, siempre con conexión y siempre desde el
 * escritorio de quien administra. Meterla en el canal de sync le agregaría a
 * cada dispositivo el peso de replicar algo que nadie edita offline.
 *
 * La agenda —que sí se usa todo el día y sin red— sigue leyendo de Dexie.
 */

const API = '/api'

interface Horario {
  id: string
  diaSemana: number
  desde: string
  hasta: string
}

interface Recurso {
  id: string
  nombre: string
  recursoTipoId: string
  activo: boolean
  documento: string | null
  registroProfesional: string | null
  especialidad: string | null
  telefono: string | null
  email: string | null
  biografia: string | null
  horarios: Horario[]
}

interface Tipo {
  id: string
  nombre: string
  esPersona: boolean
  /** Espacio físico asignable por franja (§3.8). Un equipo no es ni lo uno ni lo otro. */
  esEspacio: boolean
  seleccionablePorCliente: boolean
  bufferMin: number
}

interface Servicio {
  id: string
  nombre: string
  precio: number
  reservableOnline: boolean
  activo: boolean
}

interface Config {
  listaParaOperar: boolean
  pendientes: string[]
  sucursales: { id: string; nombre: string }[]
  tipos: Tipo[]
  recursos: Recurso[]
  servicios: Servicio[]
  flags: string[]
}

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']

export function Configuracion({ slotMin }: { slotMin: number }) {
  const [config, setConfig] = useState<Config | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cargando, setCargando] = useState(true)

  const recargar = useCallback(async () => {
    try {
      const res = await fetch(`${API}/config`, { credentials: 'include' })
      if (!res.ok) throw new Error('No pudimos leer la configuración')
      setConfig((await res.json()) as Config)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo salió mal')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void recargar()
  }, [recargar])

  async function enviar(ruta: string, metodo: string, cuerpo?: unknown) {
    setError(null)
    const res = await fetch(`${API}${ruta}`, {
      method: metodo,
      credentials: 'include',
      ...(cuerpo ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(cuerpo) } : {}),
    })
    if (!res.ok) {
      const detalle = (await res.json().catch(() => ({}))) as { mensaje?: string }
      setError(detalle.mensaje ?? 'No pudimos guardar el cambio')
      return false
    }
    await recargar()
    return true
  }

  if (cargando) return <p className="p-8 text-center text-slate-500">Cargando configuración…</p>
  if (!config) {
    return (
      <p className="p-8 text-center text-rose-700">{error ?? 'No pudimos leer la configuración'}</p>
    )
  }

  return (
    <div>
      <TituloPagina
        titulo="Configuración"
        bajada="Cómo está armada tu clínica. Lo que cambies acá afecta a toda la agenda."
      />

      {error && (
        <p role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {/* Lo primero: qué falta para poder operar. Una clínica a medio configurar
          no da error, simplemente no ofrece turnos, y desde recepción eso se ve
          idéntico a "no hay lugar". */}
      {config.listaParaOperar ? (
        <p className="mb-6 rounded-lg bg-teal-50 p-3 text-sm text-teal-900">
          La clínica está lista para operar.
        </p>
      ) : (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900">Falta para poder agendar:</p>
          <ul className="mt-1 list-inside list-disc text-sm text-amber-900">
            {config.pendientes.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <SeccionTipos tipos={config.tipos} onCrear={(t) => enviar('/config/tipos-recurso', 'POST', t)} />

      <CatalogoEspecialidades />

      <SeccionRecursos
        titulo="Profesionales"
        ayuda="Quiénes atienden. Cada uno necesita horario cargado: sin horario nunca aparece disponible."
        ejemplo="Dra. Benítez"
        etiquetaAgregar="Agregar profesional"
        soloPersonas
        config={config}
        onCrear={(r) => enviar('/config/recursos', 'POST', r)}
        onEditar={(id, r) => enviar(`/config/recursos/${id}`, 'PATCH', r)}
        onDesactivar={(id) => enviar(`/config/recursos/${id}`, 'DELETE')}
        onReactivar={(id) => enviar(`/config/recursos/${id}`, 'PATCH', { activo: true })}
        onCrearHorario={(h) => enviar('/config/horarios', 'POST', h)}
        onBorrarHorario={(id) => enviar(`/config/horarios/${id}`, 'DELETE')}
      />

      <SeccionRecursos
        titulo="Consultorios y equipos"
        ayuda="Los espacios que limitan tu capacidad real. Es lo que hace que la agenda no prometa dos citas para el mismo sillón."
        ejemplo="Consultorio 2"
        etiquetaAgregar="Agregar consultorio"
        soloPersonas={false}
        config={config}
        onCrear={(r) => enviar('/config/recursos', 'POST', r)}
        onEditar={(id, r) => enviar(`/config/recursos/${id}`, 'PATCH', r)}
        onDesactivar={(id) => enviar(`/config/recursos/${id}`, 'DELETE')}
        onReactivar={(id) => enviar(`/config/recursos/${id}`, 'PATCH', { activo: true })}
        onCrearHorario={(h) => enviar('/config/horarios', 'POST', h)}
        onBorrarHorario={(id) => enviar(`/config/horarios/${id}`, 'DELETE')}
      />

      <SeccionServicios
        servicios={config.servicios}
        tipos={config.tipos}
        slotMin={slotMin}
        onCrear={(s) => enviar('/config/servicios', 'POST', s)}
        onCambiarActivo={(id, activo) => enviar(`/config/servicios/${id}`, 'PATCH', { activo })}
      />

      <section className="mt-8 border-t border-slate-200 pt-4">
        <h2 className="txt-seccion">Módulos contratados</h2>
        <p className="mb-2 text-sm text-slate-500">
          Se activan desde la administración de practiClinic, no desde acá.
        </p>
        {config.flags.length === 0 ? (
          <p className="text-sm text-slate-500">Ninguno.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {config.flags.map((f) => (
              <li key={f} className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
                {f}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function SeccionTipos({
  tipos,
  onCrear,
}: {
  tipos: Tipo[]
  onCrear: (t: unknown) => Promise<boolean>
}) {
  const [abierto, setAbierto] = useState(false)
  const [nombre, setNombre] = useState('')
  const [esPersona, setEsPersona] = useState(true)
  const [bufferMin, setBufferMin] = useState(0)

  return (
    <section className="mt-6 border-t border-slate-200 pt-4">
      <h2 className="txt-seccion">Tipos de recurso</h2>
      <p className="mb-3 text-sm text-slate-500">
        Qué clases de cosa se reservan: profesionales, consultorios, equipos.
      </p>

      <ul className="mb-3 space-y-1">
        {tipos.map((t) => (
          <li key={t.id} className="flex flex-wrap items-baseline gap-2 text-sm">
            <span className="font-medium text-slate-800">{t.nombre}</span>
            <span className="text-slate-500">{t.esPersona ? 'persona' : 'espacio'}</span>
            {t.seleccionablePorCliente && (
              <span className="rounded-full bg-teal-100 px-2 text-xs text-teal-900">
                el paciente lo elige
              </span>
            )}
            {t.bufferMin > 0 && (
              <span className="text-xs text-slate-500">{t.bufferMin} min de preparación</span>
            )}
          </li>
        ))}
      </ul>

      {abierto ? (
        <form
          className="rounded-lg border border-slate-200 p-3"
          onSubmit={async (ev) => {
            ev.preventDefault()
            const ok = await onCrear({
              nombre,
              esPersona,
              seleccionablePorCliente: esPersona,
              bufferMin: esPersona ? 0 : bufferMin,
            })
            if (ok) {
              setNombre('')
              setAbierto(false)
            }
          }}
        >
          <label className="block text-sm">
            Nombre
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
              minLength={2}
              placeholder="Odontólogo, Consultorio, Rayos X…"
              className="mt-1 min-h-10 w-full rounded-md border border-slate-300 px-2"
            />
          </label>

          <fieldset className="mt-2 text-sm">
            <legend className="text-slate-700">¿Qué es?</legend>
            <label className="mr-4">
              <input type="radio" checked={esPersona} onChange={() => setEsPersona(true)} /> Una
              persona
            </label>
            <label>
              <input type="radio" checked={!esPersona} onChange={() => setEsPersona(false)} /> Un
              espacio o equipo
            </label>
          </fieldset>

          {/* El buffer sólo aparece para espacios: es el tiempo de limpieza del
              sillón, y no tiene sentido en una persona (§3.6). */}
          {!esPersona && (
            <label className="mt-2 block text-sm">
              Minutos de preparación entre usos
              <input
                type="number"
                min={0}
                max={240}
                value={bufferMin}
                onChange={(e) => setBufferMin(Number(e.target.value))}
                className="mt-1 min-h-10 w-24 rounded-md border border-slate-300 px-2"
              />
              <span className="ml-2 text-xs text-slate-500">
                el sistema los reserva solo, nadie los agenda encima
              </span>
            </label>
          )}

          <div className="mt-3 flex gap-2">
            <Boton variante="primary" type="submit">
              Agregar
            </Boton>
            <Boton variante="ghost" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </div>
        </form>
      ) : (
        <Boton variante="secondary" onClick={() => setAbierto(true)}>
          Agregar tipo
        </Boton>
      )}
    </section>
  )
}

function SeccionRecursos({
  titulo,
  ayuda,
  ejemplo,
  etiquetaAgregar,
  soloPersonas,
  config,
  onCrear,
  onEditar,
  onDesactivar,
  onReactivar,
  onCrearHorario,
  onBorrarHorario,
}: {
  titulo: string
  ayuda: string
  ejemplo: string
  etiquetaAgregar: string
  soloPersonas: boolean
  config: Config
  onCrear: (r: unknown) => Promise<boolean>
  onEditar: (id: string, r: unknown) => Promise<boolean>
  onDesactivar: (id: string) => Promise<boolean>
  onReactivar: (id: string) => Promise<boolean>
  onCrearHorario: (h: unknown) => Promise<boolean>
  onBorrarHorario: (id: string) => Promise<boolean>
}) {
  const [nombre, setNombre] = useState('')
  const [tipoId, setTipoId] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [expandido, setExpandido] = useState<string | null>(null)

  const nombreTipo = (id: string) => config.tipos.find((t) => t.id === id)?.nombre ?? ''

  // La duración y la limpieza propias son sólo de quien el paciente elige: es el
  // único tipo de persona que tiene agenda propia por servicio (§3.6).
  const seleccionable = (id: string) =>
    config.tipos.find((t) => t.id === id)?.seleccionablePorCliente ?? false

  // Cada seccion muestra y da de alta solo lo suyo: los tipos que son persona,
  // o los que son espacio.
  const tiposPropios = config.tipos.filter((t) => t.esPersona === soloPersonas)
  const idsPropios = new Set(tiposPropios.map((t) => t.id))
  const propios = config.recursos.filter((r) => idsPropios.has(r.recursoTipoId))

  return (
    <section className="mt-8 border-t border-slate-200 pt-4">
      <h2 className="txt-seccion">{titulo}</h2>
      <p className="txt-secundario mb-4">{ayuda}</p>

      {tiposPropios.length === 0 && (
        <p className="mb-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
          Primero definí arriba un tipo de recurso que sea{' '}
          {soloPersonas ? 'una persona' : 'un espacio o equipo'}.
        </p>
      )}

      <ul className="mb-3 space-y-2">
        {propios.length === 0 && tiposPropios.length > 0 && (
          <li className="text-sm text-slate-400">Todavía no cargaste ninguno.</li>
        )}
        {propios.map((r) => (
          <li key={r.id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className={`font-medium ${r.activo ? 'text-slate-800' : 'text-slate-400'}`}>
                {r.nombre}
              </span>
              <span className="text-sm text-slate-500">{nombreTipo(r.recursoTipoId)}</span>
              {!r.activo && (
                <span className="rounded-full bg-slate-100 px-2 text-xs text-slate-600">
                  inactivo
                </span>
              )}
              {r.activo && r.horarios.length === 0 && (
                <span className="rounded-full bg-amber-100 px-2 text-xs text-amber-900">
                  sin horario
                </span>
              )}
              <button
                type="button"
                onClick={() => setExpandido(expandido === r.id ? null : r.id)}
                className="ml-auto text-sm text-teal-700"
              >
                {expandido === r.id ? 'Cerrar ficha' : soloPersonas ? 'Ficha profesional' : 'Horarios'}
              </button>
              <button
                type="button"
                onClick={() => void (r.activo ? onDesactivar(r.id) : onReactivar(r.id))}
                className="text-sm text-slate-500"
              >
                {r.activo ? 'Dar de baja' : 'Reactivar'}
              </button>
            </div>

            {expandido === r.id && (
              <div>
                {soloPersonas && <FichaProfesional recurso={r} onGuardar={onEditar} />}
                {soloPersonas && seleccionable(r.recursoTipoId) && (
                  <>
                    <EspecialidadesProfesional personaId={r.id} />
                    <DuracionesProfesional personaId={r.id} />
                  </>
                )}
                <Horarios recurso={r} onCrear={onCrearHorario} onBorrar={onBorrarHorario} />
              </div>
            )}
          </li>
        ))}
      </ul>

      {tiposPropios.length > 0 &&
        (abierto ? (
          <form
            className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4"
            onSubmit={async (ev) => {
              ev.preventDefault()
              const ok = await onCrear({ nombre, recursoTipoId: tipoId || tiposPropios[0]!.id })
              if (ok) {
                setNombre('')
                setAbierto(false)
              }
            }}
          >
            <label className="min-w-0 flex-1">
              <span className="txt-meta block">Nombre</span>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                minLength={2}
                autoFocus
                placeholder={ejemplo}
                className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-[14px]"
              />
            </label>

            {/* Con un solo tipo no se pregunta: elegir entre una opción es una
                decisión que no existe. */}
            {tiposPropios.length > 1 && (
              <label>
                <span className="txt-meta block">Tipo</span>
                <select
                  value={tipoId}
                  onChange={(e) => setTipoId(e.target.value)}
                  required
                  className="mt-1 h-9 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
                >
                  <option value="">Elegí…</option>
                  {tiposPropios.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <Boton variante="primary" type="submit">
              Agregar
            </Boton>
            <Boton variante="ghost" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </form>
        ) : (
          <Boton variante="secondary" onClick={() => setAbierto(true)}>
            {etiquetaAgregar}
          </Boton>
        ))}
    </section>
  )
}

function FichaProfesional({
  recurso,
  onGuardar,
}: {
  recurso: Recurso
  onGuardar: (id: string, cambios: unknown) => Promise<boolean>
}) {
  const [nombre, setNombre] = useState(recurso.nombre)
  const [documento, setDocumento] = useState(recurso.documento ?? '')
  const [registro, setRegistro] = useState(recurso.registroProfesional ?? '')
  const [telefono, setTelefono] = useState(recurso.telefono ?? '')
  const [email, setEmail] = useState(recurso.email ?? '')
  const [biografia, setBiografia] = useState(recurso.biografia ?? '')
  const [guardando, setGuardando] = useState(false)

  return (
    <form
      className="mt-4 rounded-xl border border-teal-100 bg-teal-50/40 p-4"
      onSubmit={async (ev) => {
        ev.preventDefault()
        setGuardando(true)
        try {
          await onGuardar(recurso.id, {
            nombre, documento: documento || null, registroProfesional: registro || null,
            telefono: telefono || null,
            email: email || null, biografia: biografia || null,
          })
        } finally {
          setGuardando(false)
        }
      }}
    >
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-700 text-base font-semibold text-white">
          {nombre.split(/\s+/).filter(Boolean).slice(0, 2).map((parte) => parte[0]).join('').toUpperCase() || 'DR'}
        </span>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Ficha profesional</h3>
          <p className="text-xs text-slate-500">Identificación, credenciales y contacto del profesional.</p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Campo etiqueta="Nombre completo *" valor={nombre} cambiar={setNombre} requerido />
        <Campo etiqueta="Documento" valor={documento} cambiar={setDocumento} placeholder="CI o pasaporte" />
        <Campo etiqueta="Matrícula / registro profesional" valor={registro} cambiar={setRegistro} placeholder="N.º de registro" />
        <Campo etiqueta="Teléfono" valor={telefono} cambiar={setTelefono} tipo="tel" />
        <Campo etiqueta="Email" valor={email} cambiar={setEmail} tipo="email" />
      </div>
      <label className="mt-3 block text-sm text-slate-700">
        Presentación profesional
        <textarea value={biografia} onChange={(e) => setBiografia(e.target.value)} maxLength={800} rows={3} placeholder="Formación, experiencia o información útil para el paciente…" className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
      </label>
      <div className="mt-3 flex justify-end">
        <Boton variante="primary" type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar ficha'}</Boton>
      </div>
    </form>
  )
}

function Campo({ etiqueta, valor, cambiar, placeholder, tipo = 'text', requerido = false }: { etiqueta: string; valor: string; cambiar: (valor: string) => void; placeholder?: string; tipo?: string; requerido?: boolean }) {
  return (
    <label className="text-sm text-slate-700">
      {etiqueta}
      <input type={tipo} value={valor} onChange={(e) => cambiar(e.target.value)} placeholder={placeholder} required={requerido} className="mt-1 min-h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm" />
    </label>
  )
}

function Horarios({
  recurso,
  onCrear,
  onBorrar,
}: {
  recurso: Recurso
  onCrear: (h: unknown) => Promise<boolean>
  onBorrar: (id: string) => Promise<boolean>
}) {
  const [dia, setDia] = useState(1)
  const [desde, setDesde] = useState('08:00')
  const [hasta, setHasta] = useState('18:00')

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      {recurso.horarios.length === 0 ? (
        <p className="mb-2 text-sm text-slate-500">Sin horarios cargados.</p>
      ) : (
        <ul className="mb-2 space-y-1">
          {recurso.horarios.map((h) => (
            <li key={h.id} className="flex items-center gap-2 text-sm">
              <span className="w-24 text-slate-700">{DIAS[h.diaSemana]}</span>
              <span className="tabular-nums text-slate-600">
                {h.desde}–{h.hasta}
              </span>
              <button
                type="button"
                onClick={() => void onBorrar(h.id)}
                className="text-slate-400 hover:text-rose-700"
                aria-label={`Quitar ${DIAS[h.diaSemana]} ${h.desde}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={async (ev) => {
          ev.preventDefault()
          await onCrear({ recursoId: recurso.id, diaSemana: dia, desde, hasta })
        }}
      >
        <select
          value={dia}
          onChange={(e) => setDia(Number(e.target.value))}
          className="min-h-10 rounded-md border border-slate-300 px-2 text-sm"
        >
          {DIAS.map((d, i) => (
            <option key={d} value={i}>
              {d}
            </option>
          ))}
        </select>
        <input
          type="time"
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
          className="min-h-10 rounded-md border border-slate-300 px-2 text-sm"
        />
        <input
          type="time"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          className="min-h-10 rounded-md border border-slate-300 px-2 text-sm"
        />
        <button type="submit" className="min-h-10 rounded-lg border border-slate-300 px-3 text-sm">
          Agregar día
        </button>
      </form>
    </div>
  )
}

function SeccionServicios({
  servicios,
  tipos,
  slotMin,
  onCrear,
  onCambiarActivo,
}: {
  servicios: Servicio[]
  tipos: Tipo[]
  slotMin: number
  onCrear: (s: unknown) => Promise<boolean>
  onCambiarActivo: (id: string, activo: boolean) => Promise<boolean>
}) {
  const [nombre, setNombre] = useState('')
  const [precio, setPrecio] = useState(0)
  const [duracion, setDuracion] = useState(slotMin * 2)
  const [online, setOnline] = useState(false)
  const [requeridos, setRequeridos] = useState<string[]>([])
  const [especialidadRequerida, setEspecialidadRequerida] = useState<string | null>(null)

  const alternar = (id: string) =>
    setRequeridos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  return (
    <section className="mt-8 border-t border-slate-200 pt-4">
      <h2 className="txt-seccion">Servicios</h2>
      <p className="mb-3 text-sm text-slate-500">
        Qué se puede agendar, cuánto dura y qué necesita ocupado al mismo tiempo.
      </p>

      <ul className="mb-3 space-y-1">
        {servicios.map((s) => (
          <li key={s.id} className="flex flex-wrap items-baseline gap-2 text-sm">
            <span className={`font-medium ${s.activo ? 'text-slate-800' : 'text-slate-400'}`}>
              {s.nombre}
            </span>
            <span className="text-slate-600 tabular-nums">{guaranies(s.precio)}</span>
            {s.reservableOnline && (
              <span className="rounded-full bg-teal-100 px-2 text-xs text-teal-900">en línea</span>
            )}
            <button
              type="button"
              onClick={() => void onCambiarActivo(s.id, !s.activo)}
              className="ml-auto text-slate-500"
            >
              {s.activo ? 'Desactivar' : 'Activar'}
            </button>
          </li>
        ))}
      </ul>

      <form
        className="rounded-lg border border-slate-200 p-3"
        onSubmit={async (ev) => {
          ev.preventDefault()
          const ok = await onCrear({
            nombre,
            precio,
            reservableOnline: online,
            especialidadRequeridaId: especialidadRequerida,
            ...(online ? { anticipacionMinH: 2, anticipacionMaxD: 60 } : {}),
            tramos: [
              {
                duracionMin: duracion,
                requisitos: requeridos.map((recursoTipoId) => ({ recursoTipoId, cantidad: 1 })),
              },
            ],
          })
          if (ok) {
            setNombre('')
            setPrecio(0)
            setRequeridos([])
            setEspecialidadRequerida(null)
          }
        }}
      >
        <div className="flex flex-wrap gap-2">
          <label className="text-sm">
            Nombre
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
              minLength={2}
              className="mt-1 block min-h-10 rounded-md border border-slate-300 px-2"
            />
          </label>
          <label className="text-sm">
            Precio (Gs.)
            <input
              type="number"
              min={0}
              step={1}
              value={precio}
              onChange={(e) => setPrecio(Number(e.target.value))}
              className="mt-1 block min-h-10 w-32 rounded-md border border-slate-300 px-2"
            />
          </label>
          <label className="text-sm">
            Duración (min)
            <input
              type="number"
              min={slotMin}
              step={slotMin}
              value={duracion}
              onChange={(e) => setDuracion(Number(e.target.value))}
              className="mt-1 block min-h-10 w-28 rounded-md border border-slate-300 px-2"
            />
          </label>
          <SelectorEspecialidadRequerida
            valor={especialidadRequerida}
            onCambiar={setEspecialidadRequerida}
          />
        </div>

        {/* El `step` ya empuja al múltiplo correcto, pero el aviso importa: un
            servicio de 20 minutos en una grilla de 15 no ofrece NUNCA un turno,
            y eso se descubre tarde y sin pistas. */}
        <p className="mt-1 text-xs text-slate-500">
          Tiene que ser múltiplo de {slotMin} minutos, que es la grilla de esta clínica.
        </p>

        <fieldset className="mt-3">
          <legend className="text-sm text-slate-700">¿Qué necesita ocupado a la vez?</legend>
          <div className="mt-1 flex flex-wrap gap-3">
            {tipos.map((t) => (
              <label key={t.id} className="text-sm">
                <input
                  type="checkbox"
                  checked={requeridos.includes(t.id)}
                  onChange={() => alternar(t.id)}
                />{' '}
                {t.nombre}
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            El turno se ofrece sólo cuando todo lo marcado está libre al mismo tiempo.
          </p>
        </fieldset>

        <label className="mt-3 block text-sm">
          <input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} /> Se
          puede pedir desde la página pública
        </label>

        <Boton variante="primary" type="submit" className="mt-4">
          Agregar servicio
        </Boton>
      </form>
    </section>
  )
}
